import { Alert } from '@amore/ui/components/alert'
import { Button } from '@amore/ui/components/button'
import { Textarea } from '@amore/ui/components/textarea'
import { Copy, Download, ExternalLink, Mail, MessageCircle } from 'lucide-react'
import { useState } from 'react'
import { formatDate } from '@/lib/format'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'

/**
 * "Svatební odkaz": one link for every guest. They open it, find their name
 * ("Kdo jste?") and reply for their whole invitation; replies land in
 * "Odpovědi z webu". The sheet copies the link, shows a QR code for printed
 * invitations and offers a ready message to send.
 */
export function ShareSheet({ onClose }: { onClose: () => void }) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const { link, published, rsvpDeadline } = data.website
  const url = `https://${link}`
  const couple = [data.identity.brideName, data.identity.groomName].filter(Boolean).join(' a ')
  const [message, setMessage] = useState(() =>
    [
      `Milí,`,
      data.identity.date
        ? `budeme se brát ${formatDate(data.identity.date, 'long')} a moc rádi bychom vás tam viděli!`
        : 'budeme se brát a moc rádi bychom vás tam viděli!',
      rsvpDeadline
        ? `Dejte nám prosím vědět do ${formatDate(rsvpDeadline, 'long')}, jestli přijdete:`
        : 'Dejte nám prosím vědět, jestli přijdete:',
      url,
      '',
      couple || 'Novomanželé',
    ].join('\n'),
  )

  const copy = (text: string, title: string) => {
    void navigator.clipboard?.writeText(text).catch(() => {})
    showToast({ title, tone: 'success' })
  }
  const publish = () => {
    update((current) => ({
      ...current,
      website: { ...current.website, published: true, rsvpEnabled: true },
    }))
    showToast({
      title: 'Web je zveřejněný',
      description: 'Hosté teď můžou odpovídat.',
      tone: 'success',
    })
  }

  return (
    <DetailSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      eyebrow="Hosté"
      title="Svatební odkaz"
      description="Jeden odkaz pro všechny. Host si na webu najde své jméno a odpoví za celou pozvánku."
    >
      <div className="divide-y divide-border [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1">
        {published ? null : (
          <div>
            <Alert tone="warning" announcement="none" className="space-y-3">
              <p>
                Web zatím není zveřejněný. Odkaz můžete poslat, ale hosté ho otevřou až po
                zveřejnění.
              </p>
              {canEdit ? (
                <Button size="sm" onClick={publish}>
                  Zveřejnit web
                </Button>
              ) : null}
            </Alert>
          </div>
        )}

        <section className="space-y-4">
          <div className="flex items-center gap-3 rounded-full bg-muted/60 py-1.5 pr-1.5 pl-5">
            <span className="min-w-0 flex-1 truncate text-[15px] font-medium">{link}</span>
            <Button size="sm" onClick={() => copy(url, 'Odkaz zkopírován')}>
              <Copy aria-hidden="true" className="size-3.5" />
              Kopírovat
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>
              {rsvpDeadline
                ? `Odpovídat můžou do ${formatDate(rsvpDeadline, 'long')}`
                : 'Termín pro odpovědi zatím nemáte'}
              <span aria-hidden="true"> · </span>
              změníte v Nastavení
            </span>
            <a
              href="/svatba"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 font-semibold text-foreground hover:underline"
            >
              Zobrazit web
              <ExternalLink aria-hidden="true" className="size-3.5" />
            </a>
          </div>
        </section>

        <section className="space-y-4">
          <h3 className="text-sm font-semibold">Zpráva pro hosty</h3>
          <Textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            aria-label="Zpráva pro hosty"
            className="field-sizing-content min-h-40 resize-none border-transparent bg-muted/60 leading-relaxed hover:bg-muted focus:bg-card"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              appearance="outline"
              tone="neutral"
              onClick={() => copy(message, 'Zpráva zkopírována')}
            >
              <Copy aria-hidden="true" className="size-3.5" />
              Kopírovat zprávu
            </Button>
            <Button
              size="sm"
              appearance="outline"
              tone="neutral"
              onClick={() =>
                showToast({
                  title: 'Otevřeli bychom WhatsApp',
                  description: 'V prototypu jen na oko.',
                })
              }
            >
              <MessageCircle aria-hidden="true" className="size-3.5" />
              WhatsApp
            </Button>
            <Button
              size="sm"
              appearance="outline"
              tone="neutral"
              onClick={() =>
                showToast({
                  title: 'Otevřeli bychom e-mail',
                  description: 'V prototypu jen na oko.',
                })
              }
            >
              <Mail aria-hidden="true" className="size-3.5" />
              E-mail
            </Button>
          </div>
        </section>

        <section className="flex items-center gap-6">
          <FakeQr seed={link} />
          <div className="space-y-3">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold">QR kód</h3>
              <p className="text-sm text-muted-foreground">
                Na tištěné pozvánky nebo oznámení. Načte se telefonem a otevře stejný odkaz.
              </p>
            </div>
            <Button
              size="sm"
              appearance="outline"
              tone="neutral"
              onClick={() => showToast({ title: 'QR kód stažen', tone: 'success' })}
            >
              <Download aria-hidden="true" className="size-3.5" />
              Stáhnout PNG
            </Button>
          </div>
        </section>
      </div>
    </DetailSheet>
  )
}

/** A QR-looking pattern for the prototype: three finder squares and stable noise from the link. */
function FakeQr({ seed }: { seed: string }) {
  const size = 25
  let hash = [...seed].reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) >>> 0, 7)
  const random = () => {
    hash = (hash * 1103515245 + 12345) >>> 0
    return hash / 2 ** 32
  }
  const inFinder = (x: number, y: number) =>
    (x < 8 && y < 8) || (x >= size - 8 && y < 8) || (x < 8 && y >= size - 8)
  const cells: [number, number][] = []
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) if (!inFinder(x, y) && random() > 0.52) cells.push([x, y])
  const finder = (x: number, y: number) => (
    <g key={`${x}-${y}`}>
      <rect x={x} y={y} width={7} height={7} rx={1.2} fill="currentColor" />
      <rect x={x + 1} y={y + 1} width={5} height={5} rx={0.8} fill="var(--card)" />
      <rect x={x + 2} y={y + 2} width={3} height={3} rx={0.6} fill="currentColor" />
    </g>
  )
  return (
    <svg
      viewBox={`-2 -2 ${size + 4} ${size + 4}`}
      className="size-32 shrink-0 rounded-xl border border-border bg-card p-1 text-foreground"
      role="img"
      aria-label="QR kód svatebního webu"
    >
      {cells.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="currentColor" />
      ))}
      {[finder(0, 0), finder(size - 7, 0), finder(0, size - 7)]}
    </svg>
  )
}
